import { FormField } from '@generated'
import {
  cityRequired,
  countryRequired,
  emailRequired,
  firstNameRequired,
  houseNumberRequired,
  internationalPostalCodeRequired,
  lastNameRequired,
  phoneRequired,
  postalCodeRequired,
  streetRequired,
} from '@lib/yup'
import { isSameDay } from 'date-fns'
import * as Yup from 'yup'
import { FormFields } from './types'

export const getField = (fields: FormFields, type: FormField) => fields.find((f) => f.type === type)

export const isFieldMandatory = (fields: FormFields, type: FormField): boolean => {
  return getField(fields, type)?.mandatory ?? false
}

export const getFieldValue = (fields: FormFields, type: FormField): any => {
  return getField(fields, type)?.value ?? null
}

export const isFieldEnabled = (fields: FormFields, type: FormField): boolean => {
  return !!getField(fields, type)
}

export const getSchema = (fields: FormFields, attendance?: boolean) => {
  let schema = Yup.object().shape({
    firstName: firstNameRequired,
    lastName: lastNameRequired,
    email: emailRequired,
    newsletterSignup: Yup.boolean().oneOf([true], 'forms.errors.privacy'),
  } as any)

  if (attendance === false) {
    schema = schema.concat(
      Yup.object().shape({
        message: Yup.string(),
      }),
    )
    return schema
  }

  // Address fields
  if (isFieldEnabled(fields, FormField.HomeAddress)) {
    const isMandatory = isFieldMandatory(fields, FormField.HomeAddress)
    const addressSchema = {
      postalCode: isMandatory
        ? Yup.string().when('country', {
            is: 'NL',
            then: () => postalCodeRequired,
            otherwise: () => internationalPostalCodeRequired,
          })
        : Yup.string(),
      country: isMandatory ? countryRequired : Yup.string(),
      city: isMandatory ? cityRequired : Yup.string(),
      street: isMandatory ? streetRequired : Yup.string(),
      houseNumber: isMandatory ? houseNumberRequired : Yup.string(),
      houseNumberSuffix: Yup.string(),
    }
    schema = schema.concat(Yup.object().shape(addressSchema))
  }

  // Phone field
  if (isFieldEnabled(fields, FormField.PhoneNumber)) {
    const isMandatory = isFieldMandatory(fields, FormField.PhoneNumber)
    schema = schema.concat(
      Yup.object().shape({
        phoneNumber: isMandatory ? phoneRequired : Yup.string(),
      }),
    )
  }

  // All other fields that can be mandatory or optional
  const fieldMappings = [
    { field: FormField.Message, name: 'message' },
    { field: FormField.DietaryRequirements, name: 'dietaryRequirements' },
    { field: FormField.NumberOfPersons, name: 'numberOfPersons' },
    { field: FormField.HeardOfGassanVia, name: 'heardOfGassanVia' },
    { field: FormField.Date, name: 'date' },
    { field: FormField.Time, name: 'time' },
    // TODO: Uncomment when FormField.Location is added to the backend
    // { field: FormField.Location, name: 'location' },
  ]

  fieldMappings.forEach(({ field, name }) => {
    if (isFieldEnabled(fields, field)) {
      const isMandatory = isFieldMandatory(fields, field)
      schema = schema.concat(
        Yup.object().shape({
          [name]: isMandatory ? Yup.string().required(`forms.errors.${name}`) : Yup.string(),
        }),
      )
    }
  })

  // Add validation for location field (independent of FormField enum for now)
  // TODO: Update when FormField.Location is added to the backend
  // For now, location is always optional
  schema = schema.concat(
    Yup.object().shape({
      location: Yup.string(),
    }),
  )

  return schema
}

export const getIsDateBlocked = (allowedDates: string[]) => {
  return (date: Date) => {
    // If no allowed dates are provided, allow all dates in the future
    if (allowedDates.length === 0) {
      return date.getTime() < new Date().setHours(0, 0, 0, 0)
    }

    for (const allowedDate of allowedDates) {
      if (isSameDay(date, new Date(allowedDate))) {
        return false
      }
    }
    return true
  }
}
